import { queryClient } from '@/config/query-client.config';
import {
  PromoBannerRequest,
  PromoBannerResponse,
  UpdatePromoBannerRequest,
} from '@repo/types/src/promo-banner';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  activatePromoBanner,
  createPromoBanner,
  deactivatePromoBanner,
  deletePromoBanner,
  getAllPromoBanners,
  updatePromoBanner,
} from './promo-banner.queries';
import { promoBannerQueryKeys } from './promo-banner.query-keys';

export const useGetPromoBanners = () => {
  const {
    data: banners,
    isError,
    isLoading,
  } = useQuery({
    queryKey: promoBannerQueryKeys.all,
    queryFn: getAllPromoBanners,
  });
  return { banners, isError, isLoading };
};

export const useCreatePromoBanner = () =>
  useMutation({
    mutationFn: (payload: PromoBannerRequest) => createPromoBanner(payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: promoBannerQueryKeys.all }),
  });

export const useUpdatePromoBanner = () =>
  useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: UpdatePromoBannerRequest;
    }) => updatePromoBanner(id, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: promoBannerQueryKeys.all }),
  });

export const useActivatePromoBanner = () =>
  useMutation({
    mutationFn: (id: number) => activatePromoBanner(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: promoBannerQueryKeys.all }),
  });

export const useDeactivatePromoBanner = () =>
  useMutation({
    mutationFn: (id: number) => deactivatePromoBanner(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: promoBannerQueryKeys.all }),
  });

export const useDeletePromoBanner = () =>
  useMutation({
    mutationFn: (id: number) => deletePromoBanner(id),
    onSuccess: (data: PromoBannerResponse) =>
      queryClient.setQueryData<PromoBannerResponse[]>(
        promoBannerQueryKeys.all,
        (old) => old?.filter((b) => b.id !== data.id),
      ),
  });
